import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Zap, Image as ImageIcon, ArrowRight, Activity, Package, ShoppingBag, MapPin,
  Gem, Trophy, Medal, BarChart3, Lock, ShoppingBag as Bag, Rocket, Compass, Crown, Unlock, Award,
  Sparkles, Boxes, CheckCircle2, Truck,
} from "lucide-react";

const BADGE_ICONS = { ShoppingBag: Bag, Package, Gem, Rocket, Compass, Crown, Unlock, Award };

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [board, setBoard] = useState([]);
  const [providers, setProviders] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [s, h, o, a, rw, lb, fp] = await Promise.all([
      api.get("/sim/state").then((r) => r.data).catch(() => null),
      api.get("/sim/history").then((r) => r.data).catch(() => []),
      api.get("/shop/orders").then((r) => r.data).catch(() => []),
      api.get("/analytics/summary").then((r) => r.data).catch(() => null),
      api.get("/rewards/me").then((r) => r.data).catch(() => null),
      api.get("/rewards/leaderboard").then((r) => r.data.leaderboard).catch(() => []),
      api.get("/fulfillment/providers").then((r) => r.data.providers).catch(() => []),
    ]);
    setState(s);
    setHistory(h);
    setOrders(o);
    setStats(a);
    setRewards(rw);
    setBoard(lb || []);
    setProviders(fp || []);
  }, []);

  useEffect(() => { load(); refresh(); }, [load, refresh]);

  const limit = state?.gen_limit ?? 0;
  const used = state?.gen_used ?? 0;
  const uncapped = limit === -1;
  const pct = uncapped ? 100 : Math.min(100, (used / Math.max(1, limit)) * 100);

  return (
    <div className="min-h-screen bg-app relative pt-10 pb-24" data-testid="dashboard-page">
      <div className="absolute inset-0 hud-grid opacity-20" />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.35em] text-cyan/70">OPERATOR CONSOLE</div>
            <h1 className="font-head text-4xl font-bold uppercase mt-1">
              Welcome, <span className="text-cyan text-glow-cyan">{user?.name?.split(" ")[0]}</span>
            </h1>
          </div>
          <button
            onClick={() => navigate("/simulation")}
            data-testid="enter-sim-btn"
            className="btn-hud flex items-center gap-2 border border-cyan text-cyan px-6 py-3 text-sm box-glow-cyan hover:bg-cyan hover:text-app"
          >
            Enter Simulation <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Quota meter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass clip-chamfer brackets p-7 lg:col-span-1"
            data-testid="quota-panel"
          >
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500">
              <Activity className="w-3.5 h-3.5 text-cyan" /> GENERATION QUOTA
            </div>
            <div className="relative flex items-center justify-center my-7">
              <svg width="170" height="170" className="-rotate-90">
                <circle cx="85" cy="85" r="72" fill="none" stroke="rgba(0,229,255,0.1)" strokeWidth="8" />
                <circle
                  cx="85" cy="85" r="72" fill="none" stroke={uncapped ? "#FFB800" : "#00E5FF"} strokeWidth="8"
                  strokeDasharray={2 * Math.PI * 72}
                  strokeDashoffset={(2 * Math.PI * 72) * (1 - pct / 100)}
                  strokeLinecap="round"
                  style={{ filter: "drop-shadow(0 0 6px rgba(0,229,255,0.6))", transition: "stroke-dashoffset 0.8s" }}
                />
              </svg>
              <div className="absolute text-center">
                <div className="font-head text-3xl font-bold text-glow-cyan" data-testid="quota-used">
                  {uncapped ? "∞" : used}
                </div>
                <div className="font-mono text-[10px] text-slate-500">{uncapped ? "UNCAPPED" : `OF ${limit}`}</div>
              </div>
            </div>
            <div className="text-center">
              <div className="font-mono text-[10px] tracking-widest text-amber">{state?.tier_name?.toUpperCase()} CLEARANCE</div>
              <button
                onClick={() => navigate("/pricing")}
                data-testid="dashboard-upgrade-btn"
                className="btn-hud mt-4 w-full border border-amber/50 text-amber py-2.5 text-xs hover:bg-amber hover:text-app"
              >
                Upgrade Clearance
              </button>
            </div>
          </motion.div>

          {/* History gallery */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass clip-chamfer p-7 lg:col-span-2"
            data-testid="history-panel"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500">
                <ImageIcon className="w-3.5 h-3.5 text-cyan" /> RENDER VAULT
              </div>
              <span className="font-mono text-[10px] text-slate-600">{history.length} ARTIFACTS</span>
            </div>
            {history.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Zap className="w-8 h-8 text-cyan/40 mb-3" />
                <p className="text-slate-500">No renders yet. Enter the simulation to generate your first world.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {history.map((g) => (
                  <div key={g.gen_id} className="group relative aspect-square overflow-hidden border border-cyan/15" data-testid="history-item">
                    <img src={g.image} alt={g.prompt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-app/95 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                      <span className="font-mono text-[9px] text-cyan/90 line-clamp-2">{g.prompt}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* ---------- Rewards: points balance + badges (entitlements live below) ---------- */}
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass clip-chamfer brackets p-7" data-testid="points-panel"
          >
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500">
              <Gem className="w-3.5 h-3.5 text-cyan" /> AETHER POINTS
            </div>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-head text-5xl font-bold text-cyan text-glow-cyan" data-testid="points-balance">
                {rewards?.points ?? 0}
              </span>
              <span className="font-mono text-[10px] text-slate-500">PTS</span>
            </div>
            <p className="font-mono text-[10px] text-slate-500 mt-2">
              {rewards?.redeem_rate || 100} pts = $1 off · earn {rewards?.points_per_dollar || 10} pts per $1 spent
            </p>

            {/* Active boosters */}
            <div className="flex flex-wrap gap-2 mt-3" data-testid="reward-boosters">
              {rewards?.multiplier_active && (
                <span className="flex items-center gap-1 font-mono text-[9px] tracking-widest px-2 py-1 border border-amber/50 text-amber" data-testid="booster-multiplier">
                  <Sparkles className="w-3 h-3" /> {rewards.multiplier}× WEEKEND
                </span>
              )}
              {rewards?.first_purchase_bonus_available && (
                <span className="flex items-center gap-1 font-mono text-[9px] tracking-widest px-2 py-1 border border-cyan/50 text-cyan" data-testid="booster-first-order">
                  <Gem className="w-3 h-3" /> +{rewards.first_purchase_bonus} FIRST ORDER
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="glass p-3 text-center">
                <div className="font-head text-xl text-slate-100">{rewards?.lifetime_points ?? 0}</div>
                <div className="font-mono text-[9px] tracking-widest text-slate-500">LIFETIME</div>
              </div>
              <div className="glass p-3 text-center">
                <div className="font-head text-xl text-amber">{rewards?.sim_score ?? 0}</div>
                <div className="font-mono text-[9px] tracking-widest text-slate-500">SIM SCORE</div>
              </div>
            </div>
            <button
              onClick={() => navigate("/shop")}
              data-testid="earn-points-btn"
              className="btn-hud mt-5 w-full flex items-center justify-center gap-2 border border-cyan/40 text-cyan py-2.5 text-xs hover:bg-cyan hover:text-app"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Earn More Points
            </button>
          </motion.div>

          {/* Badges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass clip-chamfer p-7 lg:col-span-2" data-testid="badges-panel"
          >
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500 mb-5">
              <Medal className="w-3.5 h-3.5 text-amber" /> ACHIEVEMENT SIGILS
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(rewards?.badges || []).map((b) => {
                const Icon = BADGE_ICONS[b.icon] || Award;
                return (
                  <div
                    key={b.id}
                    data-testid={`badge-${b.id}`}
                    className={`glass p-3 flex flex-col items-center text-center border transition-all ${
                      b.earned ? "border-cyan/40 box-glow-cyan" : "border-white/10 opacity-50"
                    }`}
                    title={b.desc}
                  >
                    <Icon className="w-6 h-6 mb-2" style={{ color: b.earned ? "#00E5FF" : "#64748b" }} />
                    <div className="font-head text-[11px] uppercase tracking-wide text-slate-200 leading-tight">{b.name}</div>
                    <div className="font-mono text-[8px] text-slate-500 mt-1">
                      {b.earned ? "UNLOCKED" : `${b.progress}/${b.need}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Unlocked simulations (purchase entitlements) */}
        {rewards?.unlocked_sims?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass clip-chamfer p-7 mt-6" data-testid="unlocked-sims-panel"
          >
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500 mb-5">
              <Unlock className="w-3.5 h-3.5 text-cyan" /> SECTORS UNLOCKED BY PURCHASE
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {rewards.unlocked_sims.map((s) => (
                <button
                  key={s.id}
                  onClick={() => navigate(`/simulation/world/${s.id}`)}
                  data-testid={`unlocked-sim-${s.id}`}
                  className="group relative h-28 overflow-hidden border border-cyan/15 hover:border-cyan transition-all text-left"
                >
                  <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-app via-app/50 to-transparent" />
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="font-mono text-[8px] tracking-widest" style={{ color: s.accent }}>{s.biome?.toUpperCase()}</div>
                    <div className="font-head text-sm uppercase text-slate-100">{s.name}</div>
                  </div>
                  <div className="absolute top-2 right-2 font-mono text-[8px] tracking-widest px-2 py-0.5 border border-cyan/40 text-cyan bg-app/60">OWNED</div>
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* ---------- Flywheel analytics + Leaderboard ---------- */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Conversion flywheel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="glass clip-chamfer p-7" data-testid="flywheel-panel"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500">
                <BarChart3 className="w-3.5 h-3.5 text-cyan" /> CONVERSION FLYWHEEL
              </div>
              <span className="font-mono text-[10px] text-slate-600">{stats?.total_events ?? 0} EVENTS</span>
            </div>
            <div className="space-y-3" data-testid="flywheel-funnel">
              {(stats?.funnel || []).map((f, i) => {
                const max = Math.max(1, ...(stats?.funnel || []).map((x) => x.n));
                const w = Math.max(4, (f.n / max) * 100);
                const colors = ["#00E5FF", "#22D3EE", "#A78BFA", "#FFB800"];
                const c = colors[i % colors.length];
                return (
                  <div key={f.key} data-testid={`funnel-${f.key}`}>
                    <div className="flex justify-between font-mono text-[10px] mb-1">
                      <span className="text-slate-400 tracking-wide">{f.label}</span>
                      <span style={{ color: c }}>{f.n}</span>
                    </div>
                    <div className="h-2 bg-white/5 overflow-hidden">
                      <div className="h-full transition-all duration-700" style={{ width: `${w}%`, background: c, boxShadow: `0 0 8px ${c}` }} />
                    </div>
                  </div>
                );
              })}
              {(!stats?.funnel || stats.funnel.every((f) => f.n === 0)) && (
                <p className="text-slate-500 font-mono text-[11px] py-4 text-center">
                  No funnel data yet. Browse products, open the studio, and add to cart to populate the play→design→buy loop.
                </p>
              )}
            </div>
            {stats?.top_products?.length > 0 && (
              <div className="mt-5 pt-4 border-t border-cyan/10">
                <div className="font-mono text-[9px] tracking-widest text-slate-500 mb-2">TOP VIEWED</div>
                <div className="flex flex-wrap gap-2">
                  {stats.top_products.slice(0, 4).map((p) => (
                    <span key={p.id} className="font-mono text-[9px] px-2 py-1 border border-cyan/20 text-cyan/80">
                      {p.id} · {p.count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Leaderboard */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass clip-chamfer p-7" data-testid="leaderboard-panel"
          >
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500 mb-5">
              <Trophy className="w-3.5 h-3.5 text-amber" /> GLOBAL LEADERBOARD
            </div>
            {board.length === 0 ? (
              <p className="text-slate-500 font-mono text-[11px] py-4 text-center">No explorers ranked yet. Clear a sector to claim the top slot.</p>
            ) : (
              <div className="space-y-2">
                {board.slice(0, 8).map((row) => (
                  <div
                    key={row.rank}
                    data-testid={`leaderboard-row-${row.rank}`}
                    className={`flex items-center gap-3 p-2.5 border ${row.is_me ? "border-cyan/50 bg-cyan/5 box-glow-cyan" : "border-white/8"}`}
                  >
                    <span className={`font-head text-sm w-6 text-center ${row.rank <= 3 ? "text-amber" : "text-slate-500"}`}>{row.rank}</span>
                    {row.picture ? (
                      <img src={row.picture} alt={row.name} className="w-7 h-7 rounded-full object-cover border border-white/10" />
                    ) : (
                      <div className="w-7 h-7 rounded-full border border-cyan/20 flex items-center justify-center font-head text-[10px] text-cyan">{row.name?.[0]?.toUpperCase()}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-head text-xs uppercase truncate text-slate-200">{row.name}{row.is_me && <span className="text-cyan"> · YOU</span>}</div>
                      <div className="font-mono text-[9px] text-slate-500">{row.cleared} sectors · {row.points} pts</div>
                    </div>
                    <div className="font-head text-lg" style={{ color: row.rank <= 3 ? "#FFB800" : "#00E5FF" }}>{row.score}</div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* ---------- Fulfillment network (provider-agnostic) ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="glass clip-chamfer p-7 mt-6" data-testid="fulfillment-panel"
        >
          <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500 mb-5">
            <Boxes className="w-3.5 h-3.5 text-cyan" /> FULFILLMENT NETWORK
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {providers.map((p) => (
              <div
                key={p.id}
                data-testid={`provider-${p.id}`}
                className={`glass p-4 border flex items-center gap-3 ${p.configured ? "border-cyan/40" : "border-white/10"}`}
              >
                <div className="w-9 h-9 flex items-center justify-center border" style={{ borderColor: p.configured ? "#00E5FF66" : "#ffffff22", color: p.configured ? "#00E5FF" : "#64748b" }}>
                  <Truck className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-head text-xs uppercase truncate text-slate-200">{p.name}</div>
                  <div className={`flex items-center gap-1 font-mono text-[9px] tracking-widest mt-0.5 ${p.configured ? "text-cyan" : "text-slate-500"}`}>
                    {p.configured ? <><CheckCircle2 className="w-2.5 h-2.5" /> ACTIVE</> : <><Lock className="w-2.5 h-2.5" /> PENDING KEYS</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="font-mono text-[9px] text-slate-600 mt-4">
            Orders route through a provider-agnostic layer with automatic failover to the manual partner (Helios Print Lab). Connect Printful / Amazon Merch keys to enable additional providers.
          </p>
        </motion.div>

        {/* Orders — unified account shows purchases alongside simulations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="glass clip-chamfer p-7 mt-6"
          data-testid="orders-panel"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-slate-500">
              <Package className="w-3.5 h-3.5 text-amber" /> ORDER LEDGER
            </div>
            <button
              onClick={() => navigate("/shop")}
              data-testid="dashboard-shop-btn"
              className="btn-hud flex items-center gap-1.5 border border-amber/40 text-amber px-4 py-1.5 text-xs hover:bg-amber hover:text-app"
            >
              <ShoppingBag className="w-3.5 h-3.5" /> Visit Store
            </button>
          </div>

          {orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <ShoppingBag className="w-8 h-8 text-amber/40 mb-3" />
              <p className="text-slate-500">No orders yet. Gear up at the AETHER apparel depot.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.order_id} className="glass p-4 border border-cyan/15" data-testid="order-row">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex -space-x-3">
                      {(o.items || []).slice(0, 3).map((it, idx) => (
                        <img key={idx} src={it.image} alt={it.name} className="w-12 h-12 object-cover border border-app rounded-sm" />
                      ))}
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-head text-sm uppercase text-slate-100">
                        {(o.items || []).map((i) => `${i.name} ×${i.qty}`).join(", ")}
                      </div>
                      <div className="font-mono text-[10px] text-slate-500">{o.order_id}</div>
                      {o.reward?.points_earned > 0 && (
                        <div className="flex items-center gap-1 font-mono text-[9px] text-cyan mt-0.5">
                          <Gem className="w-2.5 h-2.5" /> +{o.reward.points_earned} pts earned
                        </div>
                      )}
                      {o.fulfillment?.provider_name && (
                        <div className="flex items-center gap-1 font-mono text-[9px] text-slate-500 mt-0.5">
                          <Boxes className="w-2.5 h-2.5 text-cyan" /> {o.fulfillment.provider_name}
                        </div>
                      )}
                      {o.shipping_address && (
                        <div className="flex items-center gap-1 font-mono text-[9px] text-slate-500 mt-0.5">
                          <MapPin className="w-2.5 h-2.5 text-cyan" /> {o.shipping_address.city}, {o.shipping_address.country}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-head text-lg text-cyan">${o.amount?.toFixed(2)}</div>
                      <StatusBadge order={o} />
                    </div>
                  </div>

                  {o.payment_status === "paid" && (
                    <TrackingBar order={o} onAdvance={load} />
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function StatusBadge({ order }) {
  const paid = order.payment_status === "paid";
  const amazon = order.provider === "amazon_pay";
  const label = paid ? "PAID" : amazon ? "AMAZON PENDING" : "UNPAID";
  const color = paid ? "#00E5FF" : "#FFB800";
  return (
    <span className="font-mono text-[9px] tracking-widest px-2 py-0.5 border inline-block mt-1" style={{ color, borderColor: color + "55" }}>
      {label}
    </span>
  );
}

const TRACK_STEPS = [
  ["routed_to_print_partner", "ROUTED"],
  ["in_production", "PRINTING"],
  ["shipped", "SHIPPED"],
  ["delivered", "DELIVERED"],
];

function TrackingBar({ order, onAdvance }) {
  const [busy, setBusy] = useState(false);
  const current = order.fulfillment?.status || "routed_to_print_partner";
  const idx = TRACK_STEPS.findIndex(([k]) => k === current);
  const tracking = order.fulfillment?.tracking_number;

  const advance = async () => {
    setBusy(true);
    try {
      await api.post(`/shop/order/${order.order_id}/advance`);
      await onAdvance();
    } catch (e) { /* ignore */ } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 pt-4 border-t border-cyan/10" data-testid={`tracking-${order.order_id}`}>
      <div className="flex items-center gap-1.5">
        {TRACK_STEPS.map(([k, label], i) => (
          <React.Fragment key={k}>
            <div className="flex flex-col items-center gap-1 flex-1">
              <div className="w-full h-1.5" style={{ background: i <= idx ? "#00E5FF" : "rgba(255,255,255,0.1)", boxShadow: i <= idx ? "0 0 8px rgba(0,229,255,0.5)" : "none" }} />
              <span className="font-mono text-[8px] tracking-wider" style={{ color: i <= idx ? "#00E5FF" : "#475569" }}>{label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="font-mono text-[9px] text-slate-500">
          {tracking ? <>TRACKING: <span className="text-cyan">{tracking}</span></> : `FULFILLED VIA ${(order.fulfillment?.provider_name || "HELIOS PRINT LAB").toUpperCase()}`}
        </span>
        {idx < TRACK_STEPS.length - 1 && (
          <button
            onClick={advance}
            disabled={busy}
            data-testid={`advance-${order.order_id}`}
            className="btn-hud border border-cyan/30 text-cyan/80 px-2.5 py-1 text-[9px] hover:bg-cyan hover:text-app disabled:opacity-50"
          >
            {busy ? "..." : "SIMULATE NEXT UPDATE"}
          </button>
        )}
      </div>
    </div>
  );
}
